import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { OPERATION_TYPE } from '../../operations/models/operations.models';

/**
 * Host mínimo para validar operaciones del stepper sin acoplar DOM/scroll.
 * El componente implementa esto y pasa `onOperationInvalid` para UI.
 */
export interface WorkflowFormValidationHost {
	/** Form reactivo del editor (`general` + `operations.*`). */
	form: FormGroup;
	/** Operaciones visibles en el stepper (ya filtradas por `show`). */
	operationsFiltered: Array<{ type: string }>;
	/**
	 * Valida un sub-FormGroup ignorando el control `completed`
	 * (ese flag lo gestiona el propio stepper al marcar la op como hecha).
	 */
	formIsValidExcept(form: Partial<FormGroup>, field: string): boolean;
	/**
	 * Callback UI: marcar operación inválida y hacer scroll.
	 * Permanece en el componente (DOM / `selectAndScrollOperation`).
	 */
	onOperationInvalid(index: number, type: string): void;
}

/**
 * Validaciones de operaciones del editor de workflows (NIF-530).
 *
 * Abrir cuando: comprobar validez del stepper hasta un índice (sin DOM).
 * El foco/scroll permanece en el componente vía `onOperationInvalid`.
 * Mapa: `../WORKFLOWS_EDITOR.md`.
 */
@Injectable({
	providedIn: 'root',
})
export class WorkflowFormValidationService {
	/**
	 * Recorre las operaciones visibles desde 0 hasta `upToOperationIndex` inclusive.
	 * En la primera inválida llama a `host.onOperationInvalid` y corta.
	 *
	 * @param host - Form + lista filtrada + callbacks UI
	 * @param upToOperationIndex - Índice en `operationsFiltered` (no el índice global de ops)
	 * @returns `true` si todas las ops en el rango son válidas
	 */
	public areOperationsValid(host: WorkflowFormValidationHost, upToOperationIndex: number): boolean {
		for (let j = 0; j <= upToOperationIndex && j < host.operationsFiltered.length; j++) {
			const type = host.operationsFiltered[j].type;
			switch (type) {
				case OPERATION_TYPE.HOME:
					if (!this.isHomeOperationValid(host)) {
						host.onOperationInvalid(j, type);
						return false;
					}
					break;
				case OPERATION_TYPE.FORM:
					if (!this.isFormOperationValid(host)) {
						host.onOperationInvalid(j, type);
						return false;
					}
					break;
				case OPERATION_TYPE.OCR:
					if (!this.isOcrOperationValid(host)) {
						host.onOperationInvalid(j, type);
						return false;
					}
					break;
				case OPERATION_TYPE.POL:
					if (!this.isPolOperationValid(host)) {
						host.onOperationInvalid(j, type);
						return false;
					}
					break;
				case OPERATION_TYPE.ATTORNEY_POWERS:
					if (!this.isAttorneyPowersOperationValid(host)) {
						host.onOperationInvalid(j, type);
						return false;
					}
					break;
				case OPERATION_TYPE.DOCUMENTS:
					if (!this.isDocsOperationValid(host)) {
						host.onOperationInvalid(j, type);
						return false;
					}
					break;
				case OPERATION_TYPE.CONNECTIONS:
					if (!this.isConnectionsOperationValid(host)) {
						host.onOperationInvalid(j, type);
						return false;
					}
					break;
			}
		}
		return true;
	}

	/**
	 * Busca el primer control inválido y devuelve su path como array de segmentos
	 * (p. ej. `['operations', 'OCR', 'mode']`) para scroll/foco i18n en el componente.
	 *
	 * @returns Path o `null` si el árbol es válido / no hay hijo inválido localizable
	 */
	public findFirstInvalidControlPath(control: AbstractControl): string[] | null {
		if (control instanceof FormGroup) {
			for (const key of Object.keys(control.controls)) {
				const child = control.controls[key];
				if (child.invalid) {
					if (child instanceof FormControl) return [key];
					const sub = this.findFirstInvalidControlPath(child);
					if (sub) return [key, ...sub];
				}
			}
		}
		if (control instanceof FormArray) {
			for (let idx = 0; idx < control.controls.length; idx++) {
				const child = control.controls[idx];
				if (child.invalid) {
					if (child instanceof FormControl) return [String(idx)];
					const sub = this.findFirstInvalidControlPath(child);
					if (sub) return [String(idx), ...sub];
				}
			}
		}
		return null;
	}

	/** HOME válida ignorando el flag `completed` del stepper. */
	public isHomeOperationValid(host: WorkflowFormValidationHost): boolean {
		return host.formIsValidExcept(host.form.get(`operations.${OPERATION_TYPE.HOME}`), 'completed');
	}

	/** FORM válida ignorando `completed`. */
	public isFormOperationValid(host: WorkflowFormValidationHost): boolean {
		return host.formIsValidExcept(host.form.get(`operations.${OPERATION_TYPE.FORM}`), 'completed');
	}

	/**
	 * OCR: form válida (sin `completed`) **y**, si la op está activa,
	 * al menos un documento (DNI / pasaporte / NIE) marcado.
	 * Marca touched en los flags de doc si faltan, para mostrar errores en UI.
	 */
	public isOcrOperationValid(host: WorkflowFormValidationHost): boolean {
		const isFormValid = host.formIsValidExcept(host.form.get(`operations.${OPERATION_TYPE.OCR}`), 'completed');
		if (!isFormValid) {
			host.form.get(`operations.${OPERATION_TYPE.OCR}`).markAllAsTouched();
			return false;
		}

		// Regla de negocio: al menos un doc físico o EUDI Wallet (NIF-960).
		if (host.form.get(`operations.${OPERATION_TYPE.OCR}.available`).value) {
			if (!this.isOcrDocsSelected(host.form)) {
				host.form.get(`operations.${OPERATION_TYPE.OCR}.dni_available`).markAsTouched();
				host.form.get(`operations.${OPERATION_TYPE.OCR}.passport_available`).markAsTouched();
				host.form.get(`operations.${OPERATION_TYPE.OCR}.nie_available`).markAsTouched();
				host.form.get(`operations.${OPERATION_TYPE.OCR}.eudi_wallet_enabled`)?.markAsTouched();
				return false;
			}
		}
		return true;
	}

	/**
	 * @returns `true` si DNI, pasaporte, NIE o EUDI Wallet está activo en el form OCR
	 */
	public isOcrDocsSelected(form: FormGroup): boolean {
		return (
			!!form.get(`operations.${OPERATION_TYPE.OCR}.dni_available`)?.value ||
			!!form.get(`operations.${OPERATION_TYPE.OCR}.passport_available`)?.value ||
			!!form.get(`operations.${OPERATION_TYPE.OCR}.nie_available`)?.value ||
			!!form.get(`operations.${OPERATION_TYPE.OCR}.eudi_wallet_enabled`)?.value
		);
	}

	/** POL válida ignorando `completed`. */
	public isPolOperationValid(host: WorkflowFormValidationHost): boolean {
		return host.formIsValidExcept(host.form.get(`operations.${OPERATION_TYPE.POL}`), 'completed');
	}

	/** ATTORNEY_POWERS válida ignorando `completed`. */
	public isAttorneyPowersOperationValid(host: WorkflowFormValidationHost): boolean {
		return host.formIsValidExcept(host.form.get(`operations.${OPERATION_TYPE.ATTORNEY_POWERS}`), 'completed');
	}

	/** DOCUMENTS válida ignorando `completed`. */
	public isDocsOperationValid(host: WorkflowFormValidationHost): boolean {
		return host.formIsValidExcept(host.form.get(`operations.${OPERATION_TYPE.DOCUMENTS}`), 'completed');
	}

	/** CONNECTIONS (evaluación financiera) válida ignorando `completed`. */
	public isConnectionsOperationValid(host: WorkflowFormValidationHost): boolean {
		return host.formIsValidExcept(host.form.get(`operations.${OPERATION_TYPE.CONNECTIONS}`), 'completed');
	}
}
